/**
 * Shared TypeScript contracts for AP invoices (Invoice Explorer + Workbench).
 *
 * Mirrors `app/schemas/ap_invoices.py` (InvoiceRow) and the enums in
 * `app/models/ap.py`. Other pages import from here — do not fork these types.
 */

import { displayLocale } from '../display-locale.ts'

export type InvoiceStatus =
  | 'ingested'
  | 'matching'
  | 'needs_review'
  | 'pending_vendor_response'
  | 'pending_internal_response'
  | 'on_hold'
  | 'matched'
  | 'qa_approval'
  | 'posted'
  | 'pending_payment'
  | 'paid'
  | 'rejected'

export const INVOICE_STATUSES: InvoiceStatus[] = [
  'ingested',
  'matching',
  'needs_review',
  'pending_vendor_response',
  'pending_internal_response',
  'on_hold',
  'matched',
  'qa_approval',
  'posted',
  'pending_payment',
  'paid',
  'rejected',
]

export type ReviewCategory =
  | 'ai_flagged'
  | 'ai_rejected'
  | 'missing_document'
  | 'policy_violation'
  | 'soft_discrepancy'
  | 'data_mismatch'
  | 'system_error'
  | 'non_po'
  | 'evidence_exception'
  | 'contract_exception'
  | 'po_overbilled'

/** Explorer tab keys (cut-list #7 + the SAP "Posted" queue). Emitted as `?queue=` on click-through (R14). */
export type ExplorerTab = 'all' | 'needs_review' | 'duplicates' | 'rejected' | 'matched' | 'posted' | 'payments'

export const EXPLORER_TABS: ExplorerTab[] = ['all', 'needs_review', 'duplicates', 'rejected', 'matched', 'posted', 'payments']

/** Statuses a payment batch can be created from (mirrors PAYMENT_BATCH_SOURCE_STATUSES). */
export const PAYMENT_BATCH_SOURCE_STATUSES: InvoiceStatus[] = ['posted']

export interface TraceTally {
  passed: number
  warnings: number
  failed: number
  info: number
}

/** One grid row of the Invoice Explorer — the list contract, NOT the full invoice. */
export interface InvoiceRow {
  id: number
  invoice_id: string
  vendor_name: string | null
  invoice_date: string | null
  due_date: string | null
  grand_total: number | null
  currency: string | null
  status: InvoiceStatus
  review_category: ReviewCategory | string | null
  related_po_numbers: string[]
  related_grn_numbers: string[]
  /** POs / GRNs actually linked (what matching used); preferred over the printed references. */
  linked_po_numbers?: string[]
  linked_grn_numbers?: string[]
  match_confidence: number | null
  match_trace_tally: TraceTally
  is_duplicate: boolean
  duplicate_of_invoice_id: string | null
  sla_breached: boolean
  hold_until: string | null
  payment_batch_id: string | null
  paid_date: string | null
  /** A Synthetic (demo) PO or GRN backs this invoice (plan docs/revamp-new/02 §3.3). */
  has_synthetic_documents?: boolean
  updated_at: string
  created_at: string
  version: number
  /** Source language badge (plan 03 WP-WB-11); the label is resolved server-side from data. */
  source_language_code?: string | null
  source_language_name?: string | null
  source_language_label?: string | null
  /** True when the values were translated from a non-English document. */
  is_translated?: boolean
  /** "email" when the invoice came from an emailed attachment (plan 01 §3.3). */
  intake_channel?: 'email' | string | null
  /** Sender display name only, never the address. */
  intake_sender_name?: string | null
  intake_received_at?: string | null
}

export interface PaginationMeta {
  page: number
  page_size: number
  total_items: number
  total_pages: number
}

export interface InvoiceListResponse {
  data: InvoiceRow[]
  pagination: PaginationMeta
}

export interface TabCountsResponse {
  counts: Record<string, number>
  total: number
}

export interface QueueSummaryResponse {
  total_count: number
  /** In `reporting_currency`, converted per invoice server-side; `unconverted_count` invoices left out. */
  total_value: number
  reporting_currency?: string | null
  unconverted_count?: number
  avg_processing_time: number | null
  exception_count: number
  oldest_invoice_days: number | null
  exception_breakdown?: Record<string, number> | null
  average_age_days?: number | null
  potential_discounts?: number | null
}

export interface FilterOptionsResponse {
  vendors: string[]
  review_categories: string[]
  po_numbers: string[]
  currencies: string[]
}

export interface TabConfig {
  value: ExplorerTab | string
  label: string
  statuses: InvoiceStatus[]
  is_duplicate?: boolean | null
  is_success?: boolean
}

export interface TabConfigResponse {
  tabs: TabConfig[]
}

export interface BlockedItem {
  invoice_id: number
  current_status: InvoiceStatus | string
  reason: string
  policy_id?: string | null
}

export interface BatchResult {
  updated_count: number
  blocked: BlockedItem[]
}

export interface PaymentBatchResult {
  batch_id: string
  processed_invoice_count: number
  total_amount: number
  blocked: BlockedItem[]
}

export type InvoiceSortField = 'invoice_date' | 'due_date' | 'grand_total' | 'updated_at' | 'created_at' | 'vendor_name'
export type SortOrder = 'asc' | 'desc'

/** Build the Workbench URL for a row, always carrying the originating queue (R14). */
export function workbenchHref(invoiceId: number, queue: ExplorerTab | string): string {
  return `/workbench?invoice=${invoiceId}&queue=${encodeURIComponent(queue)}`
}

export const STATUS_LABELS: Record<InvoiceStatus, string> = {
  ingested: 'Ingested',
  matching: 'Matching',
  needs_review: 'Needs Review',
  pending_vendor_response: 'Awaiting vendor',
  pending_internal_response: 'Awaiting approval',
  on_hold: 'On Hold',
  matched: 'Matched',
  qa_approval: 'QA Approval',
  posted: 'Posted',
  pending_payment: 'Pending Payment',
  paid: 'Paid',
  rejected: 'Rejected',
}

export const REVIEW_CATEGORY_LABELS: Record<ReviewCategory, string> = {
  ai_flagged: 'AI Flagged',
  ai_rejected: 'AI Rejected',
  missing_document: 'Missing Document',
  policy_violation: 'Policy Violation',
  soft_discrepancy: 'Soft Discrepancy',
  data_mismatch: 'Data Mismatch',
  system_error: 'System Error',
  non_po: 'Non-PO',
  evidence_exception: 'Photo Evidence',
  contract_exception: 'Contract Rate',
  po_overbilled: 'PO Over-billed',
}

export function statusLabel(status: InvoiceStatus | string): string {
  return STATUS_LABELS[status as InvoiceStatus] ?? String(status).replace(/_/g, ' ')
}

export function reviewCategoryLabel(category: string | null | undefined): string {
  if (!category) return '—'
  return REVIEW_CATEGORY_LABELS[category as ReviewCategory] ?? category.replace(/_/g, ' ')
}

// ---------------------------------------------------------------------------
// Presentation helpers (one copy — Explorer, QuickLook and Workbench share it)
// ---------------------------------------------------------------------------

export function statusColor(status: InvoiceStatus | string, isDuplicate = false): string {
  if (isDuplicate) return 'bg-orange-500/10 text-orange-700 border border-orange-200'
  switch (status) {
    case 'matched':
    case 'paid':
      return 'bg-emerald-500/10 text-emerald-700 border border-emerald-200'
    case 'posted':
      return 'bg-brand-navy/10 text-brand-navy border border-emerald-300'
    case 'pending_payment':
    case 'qa_approval':
      return 'bg-brand-cornflower/10 text-brand-navy border border-brand-cornflower/30'
    case 'needs_review':
    case 'pending_vendor_response':
    case 'pending_internal_response':
      return 'bg-amber-500/10 text-amber-700 border border-amber-200'
    case 'on_hold':
      return 'bg-purple-500/10 text-purple-700 border border-purple-200'
    case 'rejected':
      return 'bg-red-500/10 text-red-700 border border-red-200'
    case 'ingested':
    case 'matching':
      return 'bg-blue-500/10 text-blue-700 border border-blue-200'
    default:
      return 'bg-muted text-muted-foreground border border-border'
  }
}

export function statusDotColor(status: InvoiceStatus | string, isDuplicate = false): string {
  if (isDuplicate) return 'bg-orange-500'
  switch (status) {
    case 'matched':
    case 'paid':
      return 'bg-emerald-500'
    case 'posted':
      return 'bg-emerald-600'
    case 'pending_payment':
    case 'qa_approval':
      return 'bg-brand-cornflower'
    case 'needs_review':
    case 'pending_vendor_response':
    case 'pending_internal_response':
      return 'bg-amber-500'
    case 'on_hold':
      return 'bg-purple-500'
    case 'rejected':
      return 'bg-red-500'
    case 'ingested':
    case 'matching':
      return 'bg-blue-500'
    default:
      return 'bg-muted-foreground'
  }
}

export function confidenceColor(conf: number | null | undefined): string {
  if (conf == null) return 'text-muted-foreground'
  if (conf >= 0.98) return 'text-emerald-600 font-semibold'
  if (conf >= 0.9) return 'text-brand-cornflower font-semibold'
  if (conf >= 0.75) return 'text-amber-600 font-semibold'
  return 'text-red-500'
}

export interface DocumentNumbers {
  po: string[]
  grn: string[]
  poLinked: boolean
  grnLinked: boolean
}

/**
 * PO and GRN numbers to show for an invoice: the documents matching actually
 * linked when there are any, else the references as printed (which can name a
 * delivery order where the GRN belongs).
 */
export function documentNumbers(row: {
  related_po_numbers?: string[] | null
  related_grn_numbers?: string[] | null
  linked_po_numbers?: string[] | null
  linked_grn_numbers?: string[] | null
}): DocumentNumbers {
  const linkedPo = row.linked_po_numbers ?? []
  const linkedGrn = row.linked_grn_numbers ?? []
  return {
    po: linkedPo.length ? linkedPo : (row.related_po_numbers ?? []),
    grn: linkedGrn.length ? linkedGrn : (row.related_grn_numbers ?? []),
    poLinked: linkedPo.length > 0,
    grnLinked: linkedGrn.length > 0,
  }
}

/** Shown in place of a currency code the document did not state. Never a guessed default. */
export const MISSING_CURRENCY_HINT = 'Currency not stated on the document'

/** The currency code to show, or null when the invoice carries none (render a dash and the hint). */
export function currencyCode(currency: string | null | undefined): string | null {
  const code = (currency ?? '').trim().toUpperCase()
  return code || null
}

/** "MYR 1,000.00"; grouping follows the selected country's locale ("INR 1,23,456.00"). */
export function formatMoney(
  value: number | null | undefined,
  currency?: string | null,
  locale: string = displayLocale(),
): string {
  if (value == null) return '—'
  const num = value.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return currency ? `${currency} ${num}` : num
}

/** "Oct 9, 2026, 3:04 PM": the app-wide date style plus the time, for events such as ingestion jobs. */
export function formatDateTimeWithYear(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}
